import { asText } from '@prismicio/helpers';
import { PrismicRichText } from '@prismicio/react';
import { hasRichText } from '@publicss/utils';
import { gsap } from 'gsap';
import React from 'react';
import styled from 'styled-components';

import { PageBodyBlock_ContactPrimary } from '../../../types/prismic';
import { usePageContext } from '../../providers/PageContext';
import { media } from '../../styles/media';
import resolveColorCK from '../../utils/resolveColorCK';
import FormResolver from '../forms/FormResolver';

const Section = styled.section`
  position: relative;
  overflow: hidden;

  .bg--contact {
    position: absolute;
    z-index: -1;
    top: 0;
    inset-inline-start: 0;
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
    background-position: center 0;
    background-size: contain;
    overflow: hidden;
    @media ${media.smUp} {
      background-position: center -20%;
    }
    @media ${media.mdUp} {
      top: auto;
      bottom: 0;
      background-size: cover;
      background-position: bottom center;
    }
  }
  .bg--primary--left,
  .bg--primary-dark--left,
  .bg--secondary--left,
  .bg--tertiary--left {
    &::before {
      position: absolute;
      content: '';
      bottom: 0;
      inset-inline-start: -80px;
      height: 100%;
      width: 80px;
      background: var(--primary);
    }
  }
  .bg--primary-dark {
    &::before {
      background: var(--primary-dark);
    }
  }
  .bg--secondary--left {
    &::before {
      background: var(--secondary);
    }
  }
  .bg--tertiary--left {
    &::before {
      background: var(--tertiary);
    }
  }
  .bg--right {
    &::after {
      position: absolute;
      z-index: 1;
      content: '';
      bottom: 0;
      inset-inline-end: -50%;
      height: 100%;
      width: 200%;
      background: var(--grey-2);
      @media ${media.mdUp} {
        inset-inline-end: -98%;
        width: 100%;
      }
    }
  }
`;

interface BlockContactSliceProps {
  slice: {
    primary: PageBodyBlock_ContactPrimary;
  };
}

const BlockContactSlice: React.FunctionComponent<BlockContactSliceProps> = ({
  slice: {
    primary: {
      color,
      title_start,
      title_middle,
      title_end,
      background_image,
      button,
      content,
      form_title,
    },
  },
}) => {
  React.useEffect(() => {
    const fadeUp = document.querySelectorAll('#contact .fade-up');
    fadeUp.forEach(function (element) {
      gsap.from(element, {
        scrollTrigger: {
          trigger: element,
          start: 'top 90%',
        },
        duration: 0.5,
        opacity: '0',
        y: '20%',
      });
    });
    const bg = document.querySelector('.bg--contact');
    const bgTL = gsap.timeline({
      scrollTrigger: {
        trigger: '#contact',
        scrub: 1,
        start: 'top 90%',
      },
    });
    bgTL.fromTo(bg, { scale: 1.1 }, { scale: 1, force3D: true }, 0);
  }, []);

  const { siteTag } = usePageContext();

  const getColor = (color) => {
    if (siteTag !== 'site:cloudkitchens') return 'primary';
    switch (color) {
      case 'terracotta':
        return 'primary';
      case 'herb':
        return 'primary-dark';
      case 'stone':
        return 'primary-light';
      default:
        return 'primary';
    }
  };

  const colorInverse =
    color === 'herb' || color === 'terracotta'
      ? getColor('stone')
      : color === 'stone'
        ? getColor('herb')
        : '';

  const resolvedColor = getColor(color);

  return (
    <Section id="contact">
      <div className="container">
        <div
          className={`grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12 form--${resolvedColor}`}>
          <div
            className={`col-span-4 h-fit self-end md:col-span-3 lg:col-span-5 bg--${resolvedColor} pr0-md mt0--md mt-40 pb-12 pr-6 pt-12 md:pb-16 md:pt-16 lg:pb-20 lg:pt-20 bg--${resolvedColor}--left relative`}>
            <h2 className="h2 color--white fade-up">
              {asText(title_start.richText)}
              <span className={`color--${colorInverse}`}>
                {asText(title_middle.richText)}
              </span>
              {asText(title_end.richText)}
            </h2>
          </div>
          <div className="bg--grey-2 bg--right relative col-span-4 pb-20 pt-20 md:col-span-5 md:mt-24 md:pb-40 md:pt-24 lg:col-span-7 lg:col-start-6 lg:pt-24">
            <div className="relative z-10 grid grid-cols-4 md:grid-cols-7">
              <div className="col-span-4 md:col-span-6 md:col-start-2 lg:col-span-6 lg:col-start-2">
                {hasRichText(content) && (
                  <div className="fade-up mb-12">
                    <PrismicRichText field={content.richText} />
                  </div>
                )}
                <FormResolver
                  caller="BlockContactSlice"
                  buttonText={button.text}
                  color={resolveColorCK(color)}
                  form_title={form_title}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {background_image?.url && (
        <img className="bg--contact" src={background_image?.url} alt="" />
      )}
    </Section>
  );
};
export default BlockContactSlice;
